import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth, useAuthStore } from '../store/useAuthStore';

/**
 * Déconnecte automatiquement l'utilisateur après une période d'inactivité.
 *
 * @param {number}   timeoutMs  Durée totale d'inactivité avant déconnexion (ms). Défaut : 15 min.
 * @param {number}   warningMs  Durée du pré-avertissement avant déconnexion (ms). Défaut : 60 s.
 * @param {Function} onLogout   Callback appelé à la déconnexion (ex : navigate('/login')).
 */
export function useInactivityLogout({
  timeoutMs = 15 * 60 * 1000,
  warningMs = 60  * 1000,
  onLogout,
} = {}) {
  // ── Réactivité : on s'abonne au store pour détecter login/logout ──────────
  const { isAuthenticated } = useAuth();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const logoutTimer    = useRef(null);
  const warningTimer   = useRef(null);
  const countdownTimer = useRef(null);

  // Ref pour éviter que onLogout (arrow function recréée à chaque render)
  // ne déclenche une cascade de re-renders via les dépendances useCallback.
  const onLogoutRef = useRef(onLogout);
  useEffect(() => { onLogoutRef.current = onLogout; }); // pas de deps → toujours à jour

  // ── clearAll : stable (dépend uniquement de refs) ─────────────────────────
  const clearAll = useCallback(() => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownTimer.current);
    logoutTimer.current    = null;
    warningTimer.current   = null;
    countdownTimer.current = null;
  }, []); // refs = stables → pas de deps

  // ── Déconnexion effective ─────────────────────────────────────────────────
  // doLogout est stable grâce à onLogoutRef : plus de cascade de re-renders.
  const doLogout = useCallback(() => {
    clearAll();
    setShowWarning(false);
    useAuthStore.logout();
    onLogoutRef.current?.();
  }, [clearAll]);

  // ── Remettre le compteur à zéro (activité utilisateur détectée) ───────────
  const reset = useCallback(() => {
    if (!isAuthenticated) return;
    clearAll();
    setShowWarning(false);

    // Avertissement (warningMs avant la déconnexion)
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(Math.round(warningMs / 1000));

      countdownTimer.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(countdownTimer.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }, timeoutMs - warningMs);

    // Déconnexion finale
    logoutTimer.current = setTimeout(doLogout, timeoutMs);
  }, [isAuthenticated, timeoutMs, warningMs, doLogout, clearAll]);

  // ── Écoute des événements d'activité ─────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      clearAll();
      setShowWarning(false);
      return;
    }

    const EVENTS = [
      'mousemove', 'mousedown', 'keydown',
      'scroll', 'touchstart', 'click', 'pointermove',
    ];

    // Throttle : au max 1 reset toutes les 10 s pour ne pas spammer les timers
    let lastReset = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset < 10_000) return;
      lastReset = now;
      reset();
    };

    EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    reset(); // démarre le timer dès que l'utilisateur est authentifié

    return () => {
      EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
      clearAll();
    };
    // reset est stable car doLogout/clearAll sont stables.
    // isAuthenticated change réellement (via useAuth / useSyncExternalStore).
  }, [isAuthenticated, reset, clearAll]);

  return {
    showWarning,
    secondsLeft,
    stayConnected: reset,   // "Rester connecté" → remet les timers à zéro
    logout:        doLogout, // "Se déconnecter maintenant"
  };
}
